import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, useMediaQuery } from '@mui/material';
import { Table, Tag } from 'antd';

const LOCALES = { es: 'es-ES', en: 'en-GB', it: 'it-IT' };

export const formatActivityDate = (value, language) => (value
  ? new Date(value).toLocaleString(LOCALES[language] || 'es-ES', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  })
  : null);

const byDate = (key) => (a, b) => (a[key] ? Date.parse(a[key]) : 0) - (b[key] ? Date.parse(b[key]) : 0);

/** Active / inactive (with its reason) / not checked yet (machine added since the last refresh). */
export const MachineStatusTag = ({ machine }) => {
  const { t } = useTranslation();
  if (machine.active === null || machine.active === undefined) {
    return (
      <Tooltip title={t('machineStatusPendingHelp')}>
        <Tag>{t('machineStatusPending')}</Tag>
      </Tooltip>
    );
  }
  if (machine.active) return <Tag color="green">{t('machineStatusActive')}</Tag>;
  return (
    <Tooltip title={t(`machineInactiveReason_${machine.inactiveReason}`)}>
      <Tag color="orange">{t('machineStatusInactive')}</Tag>
    </Tooltip>
  );
};

/**
 * The machines of one client (expanded row of the clients table) or of all of them (machines view):
 * whether each one is active, and when its last sale and planogram reached us.
 */
export const MachinesActivityTable = ({ machines, showGroup = false, paginate = false }) => {
  const { t, i18n } = useTranslation();
  const isSmallScreen = useMediaQuery('(max-width:600px)');

  const columns = React.useMemo(() => [
    {
      title: t('machine'),
      key: 'machine',
      sorter: (a, b) => String(a.machineId).localeCompare(String(b.machineId)),
      render: (_v, m) => (m.alias && m.alias !== m.machineId ? `${m.machineId} · ${m.alias}` : m.machineId),
    },
    ...(showGroup ? [{
      title: t('group'),
      dataIndex: 'group',
      key: 'group',
      ellipsis: true,
      sorter: (a, b) => (a.group || '').localeCompare(b.group || ''),
    }] : []),
    {
      title: t('location'),
      key: 'location',
      ellipsis: true,
      render: (_v, m) => [...new Set([m.location, m.city].filter(Boolean))].join(' · '),
    },
    {
      title: t('status'),
      key: 'status',
      sorter: (a, b) => Number(Boolean(a.active)) - Number(Boolean(b.active)),
      render: (_v, m) => <MachineStatusTag machine={m} />,
    },
    {
      title: t('machineLastSale'),
      key: 'lastSaleAt',
      sorter: byDate('lastSaleAt'),
      defaultSortOrder: 'descend',
      render: (_v, m) => formatActivityDate(m.lastSaleAt, i18n.language) ?? t('machineNoSaleIn4Weeks'),
    },
    {
      title: t('machineLastPlanogram'),
      key: 'lastPlanogramAt',
      sorter: byDate('lastPlanogramAt'),
      render: (_v, m) => formatActivityDate(m.lastPlanogramAt, i18n.language) ?? t('machineNoPlanogramIn3Days'),
    },
  ], [t, i18n.language, showGroup]);

  return (
    <Table
      columns={columns}
      dataSource={machines}
      rowKey="dStoreId"
      size="small"
      scroll={{ x: 'max-content' }}
      pagination={paginate ? {
        pageSize: 50,
        simple: isSmallScreen,
        showSizeChanger: !isSmallScreen,
        showTotal: (total, range) => t('machinesPaginationTotal', { start: range[0], end: range[1], total }),
      } : false}
    />
  );
};
